// Foundations
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

// rxjs
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import {
  map,
  finalize,
  filter,
  take,
  concatMap,
  catchError,
  mergeMap,
  switchMap,
  defaultIfEmpty,
} from 'rxjs/operators';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import {
  Allergy,
  Pet,
  PetVaccination,
  UpdatePetGQL,
  Vaccine,
} from '../../../services/graphql.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { S3Service } from '@app/services/s3.service';
import { AuthService } from '@app/auth/auth.service';
import { DialogService } from '@app/services/dialog.service';

// Components
import { ConfirmDialogComponent } from '@app/components/_share/confirm-dialog/confirm-dialog.component';

// Models
import { ParsedVet } from '@app/models/pet/vet.model';

// Helper function
import { cloneAbstractControl } from '@app/shared/clone-abstract-control';

// ENV
import { environment } from '@environments/environment';

const bucketVaccImages = environment.bucketVaccImages;

@Component({
  selector: 'app-health-records',
  templateUrl: './health-records.component.html',
  styleUrls: ['./health-records.component.scss'],
})
export class HealthRecordsComponent implements OnInit {
  @Input() petId: number;

  @ViewChild('uploader', { static: false }) uploaderElement: ElementRef;

  form: FormGroup;
  formClone: FormGroup;
  editMode: boolean;
  allergyTypeList: Allergy[] = [
    { attributeId: 4581, name: 'Topical', description: '' },
    { attributeId: 4580, name: 'Food', description: '' },
    { attributeId: 4582, name: 'Medication', description: '' },
    { attributeId: 4583, name: 'Other', description: '' },
  ];
  displayVetSearch = false;
  selectedVet: ParsedVet;
  selectedVetClone: ParsedVet;
  unverifiedDocuments = 0;

  uploadQueue$: Observable<PetVaccination[]>;
  updatePet$: Observable<Pet>;

  constructor(
    private fb: FormBuilder,
    private _analytics: AnalyticsService,
    private updatePetMutation: UpdatePetGQL,
    private loadingIndicatorService: LoadingIndicatorService,
    private authService: AuthService,
    private dialogService: DialogService,
    private s3Service: S3Service,
    private dialog: MatDialog,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      vetId: [''],
      imgFiles: this.fb.array([]),
      allergyArr: this.fb.array([]),
      vaccineArr: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    const unverifiedDocuments =
      +this.route.snapshot.queryParams.unverifiedDocuments;

    if (!isNaN(unverifiedDocuments)) {
      this.unverifiedDocuments = unverifiedDocuments;
    }
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f(): any {
    return this.form.controls;
  }

  populateForms(pet: Partial<Pet>) {
    const { vets, vaccines, allergies } = pet;
    // Veterinarian Details
    for (const vet of vets) {
      if (vet.isPrimary) {
        const {
          id,
          practiceName,
          address1,
          address2,
          city,
          state,
          zipcode,
          email,
          phones,
        } = vet.vet;
        const telephone = phones?.length > 0 ? phones[0].phoneNumber : '';
        this.selectedVet = {
          id,
          practiceName,
          address1,
          address2,
          city,
          state,
          zipcode,
          email,
          telephone,
        };
        this.selectedVetClone = { ...this.selectedVet };
        this.form.patchValue({ vetId: id });
        break;
      }
    }
    // Vaccinations
    this.vaccineFormArray.clear();
    vaccines.forEach((vaccineInfo) => {
      const { vaccine, memo, expiresOn, vaccinationStatus } = vaccineInfo;
      this.vaccineFormArray.push(
        this.createVaccineInfo(vaccine, memo, expiresOn, vaccinationStatus)
      );
    });
    // Allergies
    this.allergyFormArray.clear();
    allergies.forEach((allergy) => {
      const { attributeId, name, description } = allergy;
      this.allergyFormArray.push(
        this.createAllergy(attributeId, name, description)
      );
    });
    this.editMode = this.form.invalid;
    this.formClone = cloneAbstractControl(this.form);
  }

  resetFormToDefault() {
    this.form = cloneAbstractControl(this.formClone);
    this.selectedVet = this.selectedVetClone;
  }

  enterEditMode(): void {
    this.editMode = true;
  }

  onSave(): void {
    this._analytics.logButtonTap('dogProfile_saveStepHealthRecord');

    this.updatePetData();
  }

  get allergyFormArray(): FormArray {
    return this.form.get('allergyArr') as FormArray;
  }

  get vaccineFormArray(): FormArray {
    return this.form.get('vaccineArr') as FormArray;
  }

  addAllergy() {
    this._analytics.logButtonTap('dogProfile_editAllergies');

    this.allergyFormArray.push(this.createAllergy());
  }

  removeAllergy(index) {
    this.allergyFormArray.removeAt(index);
  }

  getAllergyDisplayType(allergy): string {
    let display = allergy.name;
    this.allergyTypeList.forEach((type) => {
      if (type.attributeId === allergy.attributeId) {
        display = type.name;
      }
    });
    return display;
  }

  // images

  get imgFileFormArray(): FormArray {
    return this.form.get('imgFiles') as FormArray;
  }

  chooseFiles(files: FileList) {
    this.imgFileFormArray.markAsDirty();
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // CBW-796-814
        const extension = file.name.split('.').pop();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
        if (allowedExtensions.indexOf(extension) > -1) {
          this.imgFileFormArray.push(
            this.fb.group({
              url:
                extension === 'pdf'
                  ? '/assets/images/ico_pdf.png'
                  : reader.result,
              ext: file.name.split('.').pop(),
              file,
            })
          );
        } else {
          this.dialogService.showAlert({
            content: 'Please select a .jpg, .jpeg, .png, or .pdf file',
          });
        }
      };
    }
  }

  removeImage(index) {
    this.imgFileFormArray.removeAt(index);
    if (this.imgFileFormArray.length <= 0) {
      this.uploaderElement.nativeElement.value = '';
    }
  }

  selectVet() {
    this._analytics.logButtonTap('dogProfile_findLocalVet');
    this.displayVetSearch = true;
  }

  handleVetChange(vet: ParsedVet): void {
    this.displayVetSearch = false;
    if (vet) {
      this.selectedVet = vet;
      this.form.patchValue({ vetId: vet.id });
      this.form.get('vetId').markAsDirty();
    }
  }

  uploadVacc() {}

  // Private Methods
  // ------------------------------------------------------------------------------------

  private createAllergy(
    attributeId: number = null,
    name: string = '',
    description: string = ''
  ) {
    const allergyFg = this.fb.group({
      attributeId,
      name,
      description,
    });

    const attributeIdCtrl = allergyFg.get('attributeId');
    const descriptionCtrl = allergyFg.get('description');

    attributeIdCtrl.valueChanges.subscribe((value) => {
      if (!!value) {
        descriptionCtrl.setValidators(Validators.required);
      } else {
        descriptionCtrl.clearValidators();
      }
      descriptionCtrl.updateValueAndValidity({ emitEvent: false });
    });

    descriptionCtrl.valueChanges.subscribe((value) => {
      if (!!value) {
        attributeIdCtrl.setValidators(Validators.required);
      } else {
        attributeIdCtrl.clearValidators();
      }
      attributeIdCtrl.updateValueAndValidity({ emitEvent: false });
    });

    return allergyFg;
  }

  private createVaccineInfo(
    vaccine: Vaccine,
    memo: string,
    expiresOn: string,
    vaccinationStatus: string
  ) {
    const today = new Date();
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setDate(oneMonthFromNow.getDate() + 30);
    const exp = new Date(expiresOn);

    return this.fb.group({
      id: vaccine.id,
      active: vaccine.active,
      name: vaccine.name,
      required: vaccine.required,
      expiresOn,
      memo,
      vaccinationStatus,
      isWarning:
        today.getTime() > exp.getTime() ||
        oneMonthFromNow.getTime() > exp.getTime(),
      expDateText: (() => {
        if (today.getTime() > exp.getTime()) {
          return 'Expired: ';
        } else if (oneMonthFromNow.getTime() > exp.getTime()) {
          return 'Expiring soon: ';
        } else {
          return 'Expires on: ';
        }
      })(),
    });
  }

  /**
   * Update pet data, including the vaccination record files.
   *
   * This process includes 2 steps:
   * - If having vacc. files to upload, upload them first to S3 and get the `file URLs`.
   * - Update data to the backend, including both the pet info and the file URLs if available.
   */
  private updatePetData() {
    this.loadingIndicatorService.show();

    /**
     * Prep the data for the update
     */
    const { vetId } = this.form.value;
    const filteredAllergyList: any[] = this.allergyFormArray.controls
      .filter((allergyFg: FormGroup) => {
        const { attributeId, description } = allergyFg.value;
        return !!attributeId && !!description;
      })
      .map((allergyFg: FormGroup) => {
        const { attributeId, description } = allergyFg.value;
        return { attributeId, description };
      });
    const vaccinationFileList: any[] = this.imgFileFormArray.value;

    /**
     * Update pet data.
     */
    const updatePetData$ = this.updatePetMutation
      .mutate({
        petId: this.petId.toString(),
        petInput: {
          allergies: filteredAllergyList,
          ...(vetId && { vets: [{ vet: +vetId, isPrimary: true }] }),
        },
      })
      .pipe(map((res) => res.data.updatePet));
    const uploadVaccinationFilesQueue$: Observable<any>[] = [];

    //
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1),
        switchMap((accountId) => {
          // Build the upload ob for each file
          const unixEpoch = new Date().getTime();
          const petId = this.petId;
          // Generate filename and get observable of upload
          for (const [index, fileEntry] of vaccinationFileList.entries()) {
            const { ext, file } = fileEntry;
            const paddedIndex = (index + 1).toString().padStart(3, '0');
            const key = `${bucketVaccImages}/vaccination-${accountId}-${petId}-${unixEpoch}-${paddedIndex}.${ext}`;
            uploadVaccinationFilesQueue$.push(
              this.s3Service.uploadVaccImg(file, this.petId, key)
            );
          }

          // Upload the vacc. files
          return forkJoin(uploadVaccinationFilesQueue$).pipe(
            defaultIfEmpty([])
          );
        }),
        switchMap(() => {
          // Update the data
          return updatePetData$;
        }),
        catchError((error) => {
          // Check if need to show a error dialog for the vacc. upload
          if (vaccinationFileList.length > 0) {
            this.showDialog(
              'Upload Failed',
              'There is some issue when uploading the vaccination records. Please try again later.'
            );
          }
          return throwError(() => error);
        }),
        finalize(() => this.loadingIndicatorService.hide())
      )
      .subscribe((pet) => {
        if (pet) {
          // Check if need to show a successful dialog for the vacc. upload
          if (vaccinationFileList.length > 0) {
            this.showDialog(
              'Upload Successful',
              'Your vaccination records have been sent to Camp and will be updated in your profile upon review.'
            );
            this.unverifiedDocuments += vaccinationFileList.length;
          }

          // Update the form
          this.populateForms(pet);
          this.imgFileFormArray.clear();
          this.form.markAsPristine();
          this.editMode = false;
        }
      });
  }

  private showDialog(title, content) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        title: title,
        content: content,
        okText: 'OK',
        okFn: () => {
          dialogRef.close();
        },
      },
      autoFocus: false,
      disableClose: true,
    });
  }
}
